import { type WnBreakFormat } from "./wheel-spin";

export const SHOW_EVENTS = [
  "auction_livestream_update",
  "chat_has_been_raided",
  "commerce_product_deleted",
  "auction_product_deleted",
  "auction_giveaway_entry_count_updated",
  "chat_chat_preferences_update",
  "chat_message",
  "chat_raid_updated",
  "auction_giveaway_won",
  "auction_giveaway_started",
  "chat_phx_close",
  "commerce_product_updated",
  "chat_raid_selected",
  "commerce_product_unpinned",
  "chat_livestream_update",
  "commerce_new_bid",
  "commerce_auction_started",
  "commerce_timed_event_ended",
  "commerce_product_pinned",
  "commerce_product_added",
  "commerce_product_sold",
  "auction_product_added",
  "chat_raid_cancelled",
  "auction_reactions",
  "commerce_auction_ended",
  "auction_giveaway_entered",
  "chat_livestream_shared",
  "auction_payment_failed",
  "commerce_livestream_update",
  "auction_product_unpinned",
  "chat_raid_started",
  "commerce_randomizer_result_event",
  "commerce_timed_event_started",
  "commerce_break_updated",
  "auction_phx_close",
  "chat_presence_diff",
  "commerce_user_joined",
  "auction_user_joined",
  "commerce_payment_succeeded",
  "commerce_phx_close",
  "commerce_payment_failed",
  "auction_livestream_view_count_updated",
  "chat_poll_updated",
  "auction_randomizer_result_event",
] as const;

export type ShowEvent = (typeof SHOW_EVENTS)[number];

const centsToAmount = (c?: number | null) => (typeof c === "number" ? c / 100 : undefined);

export const SHOW_EVENT_PARSERS = {
  chat_presence_diff: (data) => {
    return {
      type: "chat_presence_diff" as const,
      joins: Object.values(data.joins as any[]).flatMap((room: any) =>
        (room.metas as any[]).map((meta: any) => meta.user.username),
      ),
      leaves: Object.values(data.leaves as any[]).flatMap((room: any) =>
        (room.metas as any[]).map((meta: any) => meta.user.username),
      ),
    };
  },
  commerce_user_joined: (data) => {
    return {
      type: "commerce_user_joined" as const,
      user: data.username,
    };
  },
  auction_user_joined: (data) => {
    return {
      type: "auction_user_joined" as const,
      user: data.username,
    };
  },
  auction_livestream_view_count_updated: (data) => {
    return {
      type: "auction_livestream_view_count_updated" as const,
      viewCount: data.viewCount,
    };
  },
  auction_giveaway_won: (data) => {
    return {
      type: "auction_giveaway_won" as const,
      product: data?.product?.name,
      user: data?.product?.purchaserUser?.username,
      price: centsToAmount(data?.product?.soldPriceCents) ?? 0,
    };
  },
  chat_message: (data) => {
    return {
      type: "chat_message" as const,
      user: data.message.user.username,
      msg: data.message.message,
    };
  },
  commerce_payment_succeeded: (data) => {
    const price = centsToAmount(data.product.soldPriceCents);
    return {
      type: "commerce_payment_succeeded" as const,
      productId: (data?.product?.id ?? undefined) as string | undefined,
      user: data?.product?.purchaserUser?.username,
      price,
      revenueUplift: price,
      product: data.product.name,
      quantity: data?.product?.quantity?.sold,
      transactionType: data?.product?.transactionType as "AUCTION" | "BUY_IT_NOW" | undefined,
      // Known formats: 'MYSTERY' (prize wheel), 'RANDOM' (random spot break).
      // Kept wide — Whatnot can add formats; consumers gate on null vs non-null.
      breakFormat: (data?.product?.breakInfo?.breakFormat ?? null) as WnBreakFormat | null,
    };
  },
  commerce_new_bid: (data) => {
    const price = centsToAmount(data?.product?.highestBid?.price?.amount);
    const prevBid = centsToAmount(
      data?.product?.highestBid?.price?.amount -
        (data?.product?.nextBidCents - data?.product?.highestBid?.price?.amount),
    );
    return {
      type: "commerce_new_bid" as const,
      user: data.highestBidder.username,
      price,
      prevBid,
      bidUplift: (price ?? 0) - (prevBid ?? 0),
      product: data?.product?.name,
    };
  },

  commerce_auction_started: (data) => ({
    type: "commerce_auction_started" as const,
    price:
      // sometimes minimum price is present instead
      centsToAmount(data?.product?.startingPriceCents) ??
      centsToAmount(data?.product?.auctionMinimumCents),
    product: data?.product?.name,
  }),

  commerce_auction_ended: (data) => ({
    type: "commerce_auction_ended" as const,
    user: data?.product?.purchaserUser?.username,
    price:
      centsToAmount(data?.product?.soldPriceCents) ??
      centsToAmount(data?.product?.soldPrice?.amount) ??
      centsToAmount(data?.product?.highestBid?.priceCents),
    product: data?.product?.name,
    breakFormat: data?.product?.breakInfo?.breakFormat ?? null,
  }),

  commerce_product_deleted: (data) => ({
    type: "commerce_product_deleted" as const,
    product: data?.product?.name,
  }),

  commerce_product_pinned: (data) => ({
    type: "commerce_product_pinned" as const,
    product: data?.product?.name,
  }),

  commerce_payment_failed: (data) => ({
    type: "commerce_payment_failed" as const,
    user: data?.product?.purchaserUser?.username,
    product: data?.product?.name,
    reason: data?.placeOrderErrorReason ?? data?.product?.paymentErrorMessage,
  }),
  commerce_product_sold: (data) => {
    //I think no revenue generated here because it is not payment not succeeded
    const price = centsToAmount(data?.product?.soldPriceCents);
    return {
      type: "commerce_product_sold" as const,
      productId: (data?.product?.id ?? undefined) as string | undefined,
      user: data?.product?.purchaserUser?.username,
      price,
      quantity: data?.product?.quantity?.sold,
      product: data?.product?.name,
      transactionType: data?.product?.transactionType as "AUCTION" | "BUY_IT_NOW" | undefined,
      breakFormat: data?.product?.breakInfo?.breakFormat ?? null,
      paymentFailed: Boolean(data?.product?.paymentFailed),
    };
  },
  chat_livestream_shared: (data) => ({
    type: "chat_livestream_shared" as const,
    shareCounts: data.shareCounts,
  }),
  chat_has_been_raided: (data) => ({
    type: "chat_has_been_raided" as const,
    user: data.fromUser?.username,
  }),
  chat_poll_updated: (data) => ({
    type: "chat_poll_updated" as const,
    status: data.status,
    question: data.question,
    choices: data.choices,
    votes: data.votes,
  }),
  auction_randomizer_result_event: (data) => ({
    type: "auction_randomizer_result_event" as const,
    user: data.buyer_username,
    result: data.result,
    entrantsCount: data.entrants?.length,
  }),
} satisfies Partial<Record<ShowEvent, (data: any) => { type: ShowEvent }>>;

export type ParsedShowEvent = {
  [K in keyof typeof SHOW_EVENT_PARSERS]: ReturnType<(typeof SHOW_EVENT_PARSERS)[K]>;
}[keyof typeof SHOW_EVENT_PARSERS];

export function parseShowEventStrict(type: string, data: any): ParsedShowEvent | undefined {
  const parser = SHOW_EVENT_PARSERS[type as keyof typeof SHOW_EVENT_PARSERS];
  if (parser) {
    try {
      return parser(data) as ParsedShowEvent;
    } catch {}
  }
  return undefined;
}

export function parseShowEvent(
  type: string,
  data: any,
): { data: ParsedShowEvent | any; error: string | undefined } {
  if (!(type in SHOW_EVENT_PARSERS)) {
    return { data, error: `Unknown event type: ${type}` };
  }

  const parser = SHOW_EVENT_PARSERS[type as keyof typeof SHOW_EVENT_PARSERS];
  if (!parser) {
    return { data, error: undefined };
  }

  try {
    const parsedData = parser(data) as ParsedShowEvent;
    return { data: parsedData, error: undefined };
  } catch (error) {
    return {
      data,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

// ────────────────────────────────────────────────────────────────────────
// Row-based parser — reads from the typed columns the projection function
// (project-wn-event.ts) populates, falling back to `raw_data` JSONB for
// event types we don't denormalize. This is the replacement for
// parseShowEvent(type, data) once `data` is dropped from the table.
// ────────────────────────────────────────────────────────────────────────

/** Subset of whatnot_show_data fields needed by parseShowEventFromRow. */
export type ShowEventRow = {
  type: string;
  whatnot_user: string | null;
  amt_cents: number | null;
  product_id?: string | null;
  product_name: string | null;
  listing_id?: string | null;
  next_bid_cents: number | null;
  quantity_sold: number | null;
  view_count: number | null;
  share_count: number | null;
  entry_count: number | null;
  transaction_type: string | null;
  break_format: string | null;
  event_status: string | null;
  payment_failed: boolean | null;
  bidder_username: string | null;
  giveaway_id: string | null;
  message_text: string | null;
  loyalty_tier: string | null;
  product_description: string | null;
  whatnot_user_avatar_key: string | null;
  bidder_avatar_key: string | null;
  bid_timestamp_ms: number | null;
  image_keys: string[] | null;
  extra: Record<string, any> | null;
  raw_data: any;
};

/**
 * Build a `ParsedShowEvent` from typed columns (parsed types) or `raw_data`
 * (unparsed types). Produces output equivalent to `parseShowEvent(type, data)`
 * — see `scripts/verify-parser-equivalence.ts` for the proof.
 */
export function parseShowEventFromRow(row: ShowEventRow): {
  data: ParsedShowEvent | any;
  error: string | undefined;
} {
  if (!(row.type in SHOW_EVENT_PARSERS)) {
    return { data: row.raw_data, error: `Unknown event type: ${row.type}` };
  }
  try {
    const parsed = buildFromRow(row);
    if (parsed === undefined) return { data: row.raw_data, error: undefined };
    return { data: parsed, error: undefined };
  } catch (error) {
    return {
      data: row.raw_data,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

function buildFromRow(row: ShowEventRow): ParsedShowEvent | undefined {
  const rd: any = row.raw_data ?? {};
  const extra: any = row.extra ?? {};

  switch (row.type) {
    // ── PARSED types (read from typed columns) ──────────────────────

    case "chat_message":
      return {
        type: "chat_message" as const,
        user: row.whatnot_user as string,
        msg: row.message_text as string,
      };

    case "commerce_payment_succeeded": {
      const price = centsToAmount(row.amt_cents);
      return {
        type: "commerce_payment_succeeded" as const,
        productId: row.product_id ?? undefined,
        user: row.whatnot_user ?? undefined,
        price,
        revenueUplift: price,
        product: row.product_name ?? undefined,
        quantity: row.quantity_sold ?? undefined,
        transactionType: (row.transaction_type ?? undefined) as
          | "AUCTION"
          | "BUY_IT_NOW"
          | undefined,
        breakFormat: (row.break_format ?? null) as WnBreakFormat | null,
      };
    }

    case "commerce_new_bid": {
      const price = centsToAmount(row.amt_cents);
      // Original parser: prevBid = highestBid - (nextBid - highestBid)
      //                        = 2 * highestBid - nextBid
      const prevBidCents =
        row.amt_cents != null && row.next_bid_cents != null
          ? 2 * row.amt_cents - row.next_bid_cents
          : null;
      const prevBid = centsToAmount(prevBidCents);
      return {
        type: "commerce_new_bid" as const,
        user: row.whatnot_user as string,
        price,
        prevBid,
        bidUplift: (price ?? 0) - (prevBid ?? 0),
        product: row.product_name ?? undefined,
      };
    }

    case "commerce_auction_started":
      return {
        type: "commerce_auction_started" as const,
        // amt_cents = startingPriceCents ?? auctionMinimumCents
        // (set by fillUserAndAmount).
        price: centsToAmount(row.amt_cents),
        product: row.product_name ?? undefined,
      };

    case "commerce_auction_ended":
      return {
        type: "commerce_auction_ended" as const,
        user: row.whatnot_user ?? undefined,
        // amt_cents fallback chain (soldPriceCents → soldPrice.amount →
        // highestBid.priceCents) is set by fillUserAndAmount, so no extra
        // lookups are needed here.
        price: centsToAmount(row.amt_cents),
        product: row.product_name ?? undefined,
        breakFormat: row.break_format ?? null,
      };

    case "commerce_product_pinned":
      return {
        type: "commerce_product_pinned" as const,
        product: row.product_name ?? undefined,
      };

    case "commerce_payment_failed":
      return {
        type: "commerce_payment_failed" as const,
        user: row.whatnot_user ?? undefined,
        product: row.product_name ?? undefined,
        reason: extra.place_order_error_reason ?? row.message_text ?? undefined,
      };

    case "commerce_product_sold":
      return {
        type: "commerce_product_sold" as const,
        productId: row.product_id ?? undefined,
        user: row.whatnot_user ?? undefined,
        price: centsToAmount(row.amt_cents),
        quantity: row.quantity_sold ?? undefined,
        product: row.product_name ?? undefined,
        transactionType: (row.transaction_type ?? undefined) as
          | "AUCTION"
          | "BUY_IT_NOW"
          | undefined,
        breakFormat: row.break_format ?? null,
        paymentFailed: Boolean(row.payment_failed),
      };

    case "auction_giveaway_won":
      return {
        type: "auction_giveaway_won" as const,
        product: row.product_name ?? undefined,
        user: row.whatnot_user ?? undefined,
        price: centsToAmount(row.amt_cents) ?? 0,
      };

    case "chat_livestream_shared":
      return {
        type: "chat_livestream_shared" as const,
        shareCounts: row.share_count as number,
      };

    case "auction_livestream_view_count_updated":
      return {
        type: "auction_livestream_view_count_updated" as const,
        viewCount: row.view_count as number,
      };

    case "auction_randomizer_result_event":
      return {
        type: "auction_randomizer_result_event" as const,
        user: row.whatnot_user as string,
        result: extra.result,
        entrantsCount: row.entry_count ?? undefined,
      };

    // ── UNPARSED types (read from raw_data) ─────────────────────────

    case "commerce_user_joined":
      return {
        type: "commerce_user_joined" as const,
        user: row.whatnot_user as string,
      };

    case "auction_user_joined":
      return {
        type: "auction_user_joined" as const,
        user: row.whatnot_user as string,
      };

    case "chat_has_been_raided":
      return {
        type: "chat_has_been_raided" as const,
        user: row.whatnot_user ?? undefined,
      };

    case "commerce_product_deleted":
      // Unparsed in projection — read product name from raw_data.
      return {
        type: "commerce_product_deleted" as const,
        product: rd?.product?.name,
      };

    case "chat_presence_diff": {
      const collect = (side: any): string[] =>
        Object.values(side ?? {}).flatMap((room: any) =>
          (room?.metas ?? []).map((m: any) => m?.user?.username),
        );
      return {
        type: "chat_presence_diff" as const,
        joins: collect(rd?.joins),
        leaves: collect(rd?.leaves),
      };
    }

    case "chat_poll_updated":
      return {
        type: "chat_poll_updated" as const,
        status: rd?.status,
        question: rd?.question,
        choices: rd?.choices,
        votes: rd?.votes,
      };
  }
  return undefined;
}
